import React, { useState, useEffect } from 'react';
import { Calendar, Loader2, ArrowRight } from 'lucide-react';
import SectionTitle from './SectionTitle';
import Button from './Button';
import SEO from './SEO';
import config from '../utils/config';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${config.url}/blog`);
        if (!response.ok) throw new Error('Failed to fetch blog posts');
        const data = await response.json();
        setPosts(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } catch (err) {
        setError('ब्लॉग लोड करने में समस्या आई है।');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hi-IN', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>सभी ब्लॉग</SectionTitle>
        <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
        <p className="text-lg text-gray-600">ब्लॉग लोड हो रहे हैं...</p>
      </div>
    </section>
  );
  if (error) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>सभी ब्लॉग</SectionTitle>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
          <p className="text-lg">{error}</p>
        </div>
      </div>
    </section>
  );

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <SEO 
        title="सभी ब्लॉग - सीधी सोच सीधी बात" 
        description="सीधी सोच सीधी बात के सभी ब्लॉग पोस्ट्स और लेख। राष्ट्रीय विषयों, सामाजिक मुद्दों और स्वदेशी विचारधारा पर आधारित लेख पढ़ें।" 
        keywords="ब्लॉग, लेख, राष्ट्रीय विषय, सामाजिक मुद्दे, स्वदेशी विचारधारा, सीधी सोच सीधी बात"
        url="/blog"
      />
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <SectionTitle>सभी ब्लॉग</SectionTitle>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">संग्रहित ब्लॉग पोस्ट्स और लेख</p>
        </div>
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">कोई ब्लॉग नहीं मिला</h3>
              <p className="text-gray-500">अभी तक कोई ब्लॉग पोस्ट नहीं है।</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {posts.map(post => (
              <div key={post.id} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group">
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">{post.title}</h3>
                  <div className="flex items-center text-gray-600 text-sm mb-2">
                    <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                    <span>{formatDate(post.date)}</span>
                  </div>
                  {post.author && (
                    <div className="flex items-center text-gray-600 text-sm mb-2">
                      <span className="font-semibold">{post.author}</span>
                    </div>
                  )}
                  {post.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.description}</p>
                  )}
                  <div className="mt-auto flex justify-end">
                    <Button to={`/blog/${post.id}`} size="sm" rightIcon={ArrowRight} variant="primary" className="mt-2">और पढ़ें</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogPage;